import Head from 'next/head';
import { jsonLdScriptProps } from 'react-schemaorg';

import Layout from '@/components/Layout/Vanilla';
import ContentPage from '@/components/ContentPage';
import { fetchAPI, gql } from '@/lib/graphcms';
import { serialize } from '@/utils/serializeJsonLd';

export default function AcademyContentTemplate({
  relatedContent,
  academyPost,
  slug,
  preview,
}) {
  return (
    <Layout seo={academyPost} preview={preview}>
      <Head>
        <script
          {...jsonLdScriptProps({
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://graphcms.com/academy/${slug}`,
            },
            headline: serialize(academyPost.title),
            image: academyPost.coverImage.url,
            articleSection: 'Academy',
            keywords: 'academy,graphcms',
            datePublished: academyPost.publishedAt,
            dateModified: academyPost.updateAt || academyPost.publishedAt,
            publisher: {
              '@type': 'Organization',
              name: 'GraphCMS',
              logo: {
                '@type': 'ImageObject',
                url: 'https://graphcms.com/logo.png',
              },
            },
            description: serialize(academyPost.subTitle),
          })}
        />
      </Head>

      <ContentPage
        relatedContent={relatedContent}
        page={academyPost}
        linkSubpath="academy"
      />
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false, params }) => {
  const { academyPost, relatedContent } =
    (await fetchAPI(
      gql`
        fragment seoData on Seo {
          changefreq
          description
          image {
            url
            width
            height
            mimeType
          }
          keywords
          noindex
          priority
          title
        }

        fragment assetDataXL on Asset {
          id
          url(transformation: { image: { resize: { width: 960, fit: clip } } })
          handle
          width
          height
          mimeType
          fileName
          alt
        }

        fragment assetDataXSNoPlaceholder on Asset {
          id
          url(transformation: { image: { resize: { width: 300, fit: clip } } })
          handle
          width
          height
          mimeType
          fileName
          alt
        }

        fragment authorData on Author {
          name
          title
          twitterHandle
          bio
          photo {
            ...assetDataXSNoPlaceholder
          }
        }

        query academyContentPage($slug: String!) {
          academyPost: academyPost(where: { slug: $slug }) {
            id
            slug
            title
            subTitle
            coverImage {
              ...assetDataXL
            }
            authors {
              ...authorData
            }
            content
            externalUrl
            meta: seo {
              ...seoData
            }
            __typename
            updatedAt
            publishedAt
          }
          relatedContent: academyPosts(where: { slug_not: $slug }, first: 2) {
            id
            slug
            title
            subTitle
            coverImage {
              id
              url(
                transformation: { image: { resize: { width: 950, fit: clip } } }
              )
              handle
              width
              height
              alt
            }
            authors {
              name
              title
              twitterHandle
              photo {
                id
                url(
                  transformation: {
                    image: { resize: { width: 150, fit: clip } }
                  }
                )
                handle
                width
                height
                alt
              }
            }
            content
            externalUrl
          }
        }
      `,
      {
        preview,
        variables: {
          slug: params.slug,
        },
      },
    )) || [];

  if (!academyPost || !relatedContent) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      preview,
      academyPost,
      relatedContent,
      slug: params.slug,
    },
    revalidate: 3600,
  };
};

export const getStaticPaths = async () =>
  Promise.resolve({
    paths: [],
    fallback: `blocking`,
  });
